import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, furnitureTypesApi, locationsApi } from '../services/api';
import { useCache } from '../hooks/useOffline';
import {
  Package,
  TrendingUp,
  Building2,
  ArrowLeftRight,
  AlertTriangle,
  CheckCircle,
  Wrench,
  XCircle,
  ArrowRight,
  Download,
  Database,
  Loader2,
  MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#2563eb', '#059669', '#d97706', '#e11d48', '#8b5cf6'];

const STATUS_LABELS = {
  neuf: 'Neuf',
  bon: 'Bon',
  usage: 'Usagé',
  hors_service: 'Hors service',
  en_maintenance: 'En maintenance'
};

const MOVEMENT_LABELS = {
  entree: 'Entrée',
  sortie: 'Sortie',
  transfert: 'Transfert',
  maintenance: 'Maintenance',
  mise_au_rebut: 'Mise au rebut'
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeLabels, setTypeLabels] = useState({});
  const navigate = useNavigate();
  const { isPreCaching, cacheProgress, preCacheAll, preCacheWithFilter, cacheFreshness } = useCache();

  // Cache dialog state
  const [cacheDialogOpen, setCacheDialogOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [batiments, setBatiments] = useState([]);
  const [selectedCacheSite, setSelectedCacheSite] = useState('all');
  const [selectedCacheBatiment, setSelectedCacheBatiment] = useState('all');

  useEffect(() => {
    loadFurnitureTypes();
    loadStats();
    loadSites();
  }, []);

  // Load buildings when site changes
  useEffect(() => {
    if (selectedCacheSite && selectedCacheSite !== 'all') {
      loadBatiments(selectedCacheSite);
    } else {
      setBatiments([]);
      setSelectedCacheBatiment('all');
    }
  }, [selectedCacheSite]);

  // Load furniture types dynamically
  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      // Build labels map from types
      const labels = response.data.reduce((acc, ft) => {
        acc[ft.code] = ft.label;
        return acc;
      }, {});
      setTypeLabels(labels);
    } catch (error) {
      console.error('Erreur chargement types mobilier:', error);
    }
  };

  const loadSites = async () => {
    try {
      const response = await locationsApi.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const loadBatiments = async (siteId) => {
    try {
      const response = await locationsApi.getByParent(siteId);
      setBatiments(response.data.filter(loc => loc.type_location === 'batiment'));
    } catch (error) {
      console.error('Erreur chargement bâtiments:', error);
      setBatiments([]);
    }
  };

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCacheDialog = () => {
    setSelectedCacheSite('all');
    setSelectedCacheBatiment('all');
    setBatiments([]);
    setCacheDialogOpen(true);
  };

  const handlePreCache = async () => {
    setCacheDialogOpen(false);
    
    let locationName = 'tous les sites';
    const filters = {};
    
    if (selectedCacheSite !== 'all') {
      const site = sites.find(s => s.id === selectedCacheSite);
      locationName = site?.nom || 'le site sélectionné';
      filters.site_id = selectedCacheSite;
      
      if (selectedCacheBatiment !== 'all') {
        const batiment = batiments.find(b => b.id === selectedCacheBatiment);
        locationName = `${site?.nom} > ${batiment?.nom || 'bâtiment sélectionné'}`;
        filters.location_id = selectedCacheBatiment;
      }
    }
    
    toast.info(`Mise en cache des données pour ${locationName}...`);
    
    if (selectedCacheSite === 'all') {
      await preCacheAll();
    } else {
      await preCacheWithFilter(filters);
    }
    
    toast.success('Données mises en cache !', {
      description: 'L\'application fonctionne maintenant hors-ligne'
    });
  };

  // Count cached endpoints
  const cachedCount = Object.values(cacheFreshness).filter(f => f.isCached).length;
  const totalEndpoints = Object.keys(cacheFreshness).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statusData = stats?.furniture_by_status
    ? Object.entries(stats.furniture_by_status).map(([key, value]) => ({
        name: STATUS_LABELS[key] || key,
        value
      }))
    : [];

  const typeData = stats?.furniture_by_type
    ? Object.entries(stats.furniture_by_type).map(([key, value]) => ({
        name: typeLabels[key] || key,
        value
      }))
    : [];

  const siteData = stats?.furniture_by_site
    ? Object.entries(stats.furniture_by_site).map(([key, value]) => ({
        name: key,
        value
      }))
    : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Tableau de bord
          </h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble de votre parc mobilier</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openCacheDialog}
            variant="outline"
            disabled={isPreCaching}
            className="btn-scale"
            data-testid="precache-btn"
          >
            {isPreCaching ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Mise en cache...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Mode hors-ligne
                {cachedCount > 0 && (
                  <span className="ml-1 text-xs text-slate-400">({cachedCount}/{totalEndpoints})</span>
                )}
              </>
            )}
          </Button>
          <Button
            onClick={() => navigate('/furniture/new')}
            className="bg-blue-600 hover:bg-blue-700 btn-scale"
            data-testid="add-furniture-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Ajouter du mobilier
          </Button>
        </div>
      </div>

      {/* Cache Site Selection Dialog */}
      <Dialog open={cacheDialogOpen} onOpenChange={setCacheDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Mode hors-ligne
            </DialogTitle>
            <DialogDescription>
              Sélectionnez le site à mettre en cache pour une utilisation hors-ligne.
              Cela permet de réduire la quantité de données stockées.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cache-site">Site à mettre en cache</Label>
              <Select value={selectedCacheSite} onValueChange={setSelectedCacheSite}>
                <SelectTrigger id="cache-site" data-testid="cache-site-select">
                  <SelectValue placeholder="Sélectionnez un site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Tous les sites (complet)
                    </span>
                  </SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {site.nom}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bâtiment selector - only shown when a site is selected */}
            {selectedCacheSite !== 'all' && batiments.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="cache-batiment">Bâtiment (optionnel)</Label>
                <Select value={selectedCacheBatiment} onValueChange={setSelectedCacheBatiment}>
                  <SelectTrigger id="cache-batiment" data-testid="cache-batiment-select">
                    <SelectValue placeholder="Tous les bâtiments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Tous les bâtiments du site
                      </span>
                    </SelectItem>
                    {batiments.map(bat => (
                      <SelectItem key={bat.id} value={bat.id}>
                        <span className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {bat.nom}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {selectedCacheSite !== 'all' && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                {selectedCacheBatiment !== 'all' 
                  ? 'Seuls les mobiliers du bâtiment sélectionné seront disponibles hors-ligne.'
                  : 'Seuls les mobiliers du site sélectionné seront disponibles hors-ligne.'}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCacheDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handlePreCache} className="bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" />
              Mettre en cache
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-cache progress */}
      {isPreCaching && cacheProgress && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-700">Mise en cache: {cacheProgress.label}</span>
                  <span className="text-blue-600">{cacheProgress.completed}/{cacheProgress.total}</span>
                </div>
                <Progress value={(cacheProgress.completed / cacheProgress.total) * 100} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover" data-testid="kpi-total">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total mobilier</p>
                <p className="kpi-value mt-2">{stats?.total_furniture || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="kpi-assigned">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Mobilier affecté</p>
                <p className="kpi-value mt-2">{stats?.stock_vs_assigned?.affecte || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="kpi-stock">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">En stock</p>
                <p className="kpi-value mt-2">{stats?.stock_vs_assigned?.en_stock || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="kpi-maintenance">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">En maintenance</p>
                <p className="kpi-value mt-2">{stats?.furniture_by_status?.en_maintenance || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition par état</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Type Distribution */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Répartition par type</CardTitle>
          </CardHeader>
          <CardContent>
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={typeData} layout="vertical">
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Sites */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Movements */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Mouvements récents</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/movements')}
              className="text-blue-600"
            >
              Voir tout <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.recent_movements?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_movements.slice(0, 5).map((movement, index) => (
                  <div
                    key={movement.id || index}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      movement.type_mouvement === 'entree' ? 'bg-emerald-100 text-emerald-600' :
                      movement.type_mouvement === 'sortie' ? 'bg-rose-100 text-rose-600' :
                      movement.type_mouvement === 'maintenance' ? 'bg-orange-100 text-orange-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      <ArrowLeftRight className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {MOVEMENT_LABELS[movement.type_mouvement] || movement.type_mouvement}
                      </p>
                      <p className="text-xs text-slate-500 font-mono">
                        {new Date(movement.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                Aucun mouvement récent
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sites Distribution */}
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Mobilier par site</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/locations')}
              className="text-blue-600"
            >
              Gérer <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {siteData.length > 0 ? (
              <div className="space-y-3">
                {siteData.map((site, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-slate-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{site.name}</span>
                    </div>
                    <span className="text-sm font-mono text-slate-500">{site.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                Aucun site configuré
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
